import { format } from "date-fns"
import { formatToBRL } from "@/utils/formatToMoney"
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover"
import { ScrollArea } from "components/ui/scroll-area"
import { Separator } from "components/ui/separator"
import { Receipt, Eye, Plus, Equal, MoreHorizontal } from "lucide-react"
import type { IDivergence } from "src/@types/divergence"

type Props = {
  values: IDivergence
  conciliatedDivergences: IDivergence[]
  highlightRows: () => void
  unhighlightRows: () => void
}

export function ConciliatedDivergencesPopover({
  values,
  conciliatedDivergences,
  highlightRows,
  unhighlightRows,
}: Props) {
  // Calcular o valor total conciliado
  const totalValue = values.conciliatedDivergence?.conciliated_amount_value
    ? Number(values.conciliatedDivergence.conciliated_amount_value)
    : 0

  const getTotalColor = (value: number) => {
    if (value > 0) return "text-green-600"
    if (value < 0) return "text-red-600"
    return "text-gray-600"
  }

  // Todos os valores para o cálculo visual
  const allValues = [
    ...(Array.isArray(conciliatedDivergences) ? conciliatedDivergences.map((d) => Number(d.amount)) : []),
    Number(values.amount),
  ]

  const maxValuesShown = 3
  const showLimitedCalculation = allValues.length > maxValuesShown + 1
  const visibleValues = showLimitedCalculation
    ? [...allValues.slice(0, maxValuesShown), Number(values.amount)]
    : allValues
  const hiddenCount = showLimitedCalculation ? allValues.length - maxValuesShown - 1 : 0

  return (
    <Popover
      modal={true}
      onOpenChange={(open) => {
        if (open) highlightRows()
        else unhighlightRows()
      }}
    >
      <PopoverTrigger asChild>
        <button
          className="w-full group flex justify-center items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:text-[#402430] bg-white hover:bg-gray-50 rounded-md border border-gray-200 hover:border-[#402430] transition-all duration-200"
          type="button"
        >
          <Receipt className="w-3.5 h-3.5" />
          <span className="font-medium">
            {Array.isArray(conciliatedDivergences) ? conciliatedDivergences.length : 0} itens
          </span>
          <Eye className="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0 shadow-xl border border-gray-200 bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm text-gray-900">Conciliação</h4>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              ID: {values.conciliatedDivergence?.id || "-"}
            </span>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Lista de divergências conciliadas */}
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Itens Conciliados ({Array.isArray(conciliatedDivergences) ? conciliatedDivergences.length : 0})
            </h5>

            <ScrollArea className="h-32 w-full rounded-md border border-gray-200">
              <div className="p-2 space-y-1.5">
                {Array.isArray(conciliatedDivergences) &&
                  conciliatedDivergences.map((divergence) => (
                    <div
                      key={divergence.id}
                      className="flex justify-between items-start gap-3 p-2 bg-gray-50 rounded-md"
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className="text-sm text-gray-900 font-medium break-words leading-tight"
                          title={divergence.name}
                          style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                        >
                          {divergence.name}
                        </span>
                        {divergence.conciliation_date && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            {format(new Date(divergence.conciliation_date), "dd/MM/yyyy")}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap flex-shrink-0 ml-2">
                        {formatToBRL(Number(divergence.amount))}
                      </span>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          </div>

          {/* Divergência atual */}
          <div className="p-2 bg-[#402430]/5 rounded-md border border-[#402430]/20">
            <div className="flex justify-between items-start gap-3">
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-medium text-[#402430]">Esta Divergência</span>
                <span
                  className="text-xs text-gray-600 break-words leading-tight mt-0.5"
                  title={values.name}
                  style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                >
                  {values.name}
                </span>
              </div>
              <span className="text-sm font-medium text-[#402430] whitespace-nowrap flex-shrink-0 ml-2">
                {formatToBRL(Number(values.amount))}
              </span>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="flex items-center justify-center">
              <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Cálculo</span>
            </div>

            <div className="flex items-center justify-center gap-1 flex-wrap">
              {visibleValues.slice(0, -1).map((value, index) => (
                <div key={index} className="flex items-center gap-1">
                  <span className="text-xs font-medium text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                    {formatToBRL(value)}
                  </span>
                  <Plus className="w-2 h-2 text-gray-400" />
                </div>
              ))}

              {hiddenCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-0.5 bg-gray-100 px-1.5 py-0.5 rounded">
                    <MoreHorizontal className="w-3 h-3 text-gray-500" />
                    <span className="text-xs text-gray-500">+{hiddenCount}</span>
                  </div>
                  <Plus className="w-2 h-2 text-gray-400" />
                </div>
              )}

              <span className="text-xs font-medium text-[#402430] bg-[#402430]/10 px-1.5 py-0.5 rounded">
                {formatToBRL(Number(values.amount))}
              </span>

              <Equal className="w-3 h-3 text-gray-400 mx-1" />

              <span className={`font-bold text-sm ${getTotalColor(totalValue)} bg-white border px-2 py-1 rounded`}>
                {formatToBRL(totalValue)}
              </span>
            </div>

            {hiddenCount > 0 && (
              <div className="text-center">
                <span className="text-xs text-gray-400">
                  {hiddenCount} item{hiddenCount > 1 ? "s" : ""} oculto{hiddenCount > 1 ? "s" : ""} no cálculo
                </span>
              </div>
            )}
          </div>

          {/* Info adicional */}
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-blue-50 rounded-md p-2 border border-blue-100">
            <Eye className="w-3 h-3 text-blue-500" />
            <span>Linhas relacionadas destacadas na tabela</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
